import React, { useState } from 'react';
import { Button, Divider, Form, Input, InputNumber, Modal, Typography, Upload, Row, Col } from 'antd';
import { DatePicker } from 'antd';
import 'fragments/add-contract/add-contract.scss';
import { useLocalSession } from 'auth/helpers/session.hooks';
import { AddContractFragmentProps } from 'fragments/add-contract/interfaces';
import { useAddContractController } from 'fragments/add-contract/add-contract.controller';
import StepperForm from 'components/stepper-form/stepper-form.component';
import SelectOrCreateItem from 'components/select-or-create-item/select-or-create-item.component';
import NewOrOldContract from 'components/new-or-old-contract/new-or-old-contract.component';
import CreatePropertyFields from 'components/create-property-fields/create-property-fields.component';
import CreatePersonsFields from 'components/create-persons-fields/create-persons-fields.component';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import InterestOwed from 'components/interest-owed/interest-owed.component';
import 'moment/locale/es';
import CurrencyInput from 'components/currency-input/currency-input.component';
import { fixSelectOnScroll } from 'tools/view-hooks/fix-autocomplete';

export const AddContractFragment: React.FC<AddContractFragmentProps> = (props) => {
    const { useController = useAddContractController } = props;
    const controller = useController(props.onContractAdded, props.extractedData);
    const [getSession] = useLocalSession();
    const session = getSession();
    const { translate } = useTranslator();

    const extracted = props.extractedData;
    // The AI extracted a brand-new property with a brand-new owner: expand & prefill the
    // nested owner create fields instead of preserving them invisibly.
    const ownerCreateVisible = extracted
        ? extracted.property.existingId == null && extracted.owner.existingId == null && extracted.owner.name != null
        : false;
    // Reveal the optional person fields (phone/email/…) when AI actually provided contact data,
    // so prefilled values aren't hidden behind the "show optional fields" toggle.
    const renterHasOptionalData = !!(extracted?.renter && (extracted.renter.phone || extracted.renter.email));
    const guarantorHasOptionalData = !!(
        extracted?.guarantor &&
        (extracted.guarantor.phone || extracted.guarantor.email)
    );

    const Step1Component = (
        <>
            <Typography.Title className="title-data" level={4}>
                {translate({ key: 'contracts.title-data' })}
            </Typography.Title>
            <SelectOrCreateItem
                createFormItemName={'newProperty'}
                name={'propertyId'}
                selectOptions={controller.propertiesOptions}
                optionFilterProp={'label'}
                defaultCreateVisible={props.extractedData ? props.extractedData.property.existingId == null : false}
                createComponent={
                    <CreatePropertyFields
                        personsOptions={controller.personsOptions}
                        provincesOptions={controller.provincesOptions}
                        defaultOwnerCreateVisible={ownerCreateVisible}
                    />
                }
                createTitle={translate({ key: 'contracts.property-data' })}
                label={translate({ key: 'contracts.properties' })}
                requiredForm={true}
                rules={[
                    { required: true },
                    {
                        validator: controller.validateProperty,
                    },
                ]}
                rulesComponent={[
                    {
                        validator: (_: any, value: any, callback: any) => {
                            if (
                                !value ||
                                !value.street ||
                                !value.district ||
                                !value.city ||
                                !value.owners ||
                                (value.owners === -1 &&
                                    (!value.newOwner ||
                                        !value.newOwner.name ||
                                        !value.newOwner.lastName ||
                                        !value.newOwner.numberDoc))
                            ) {
                                callback('Complete los datos requeridos');
                                return;
                            }
                            callback();
                        },
                    },
                ]}
            />
            <SelectOrCreateItem
                name={'rentersId'}
                createFormItemName={'newRenters'}
                selectOptions={controller.personsOptions}
                selectMode={'multiple'}
                optionFilterProp={'label'}
                defaultCreateVisible={
                    props.extractedData
                        ? props.extractedData.renter.existingId == null && props.extractedData.renter.name != null
                        : false
                }
                createComponent={
                    <CreatePersonsFields
                        showOptionalFields={renterHasOptionalData}
                        provincesOptions={controller.provincesOptions}
                    />
                }
                createTitle={translate({ key: 'contracts.renter-data' })}
                onClickSelect={controller.onClickSelect}
                isOpen={controller.selectIsOpen}
                onBlurSelect={controller.onBlurSelect}
                label={translate({ key: 'contracts.renters' })}
                requiredForm={true}
                rules={[
                    { required: true },
                    {
                        validator: controller.validateRenters,
                    },
                ]}
                rulesComponent={[
                    {
                        validator: (_: any, value: any, callback: any) => {
                            if (
                                !Array.isArray(value) ||
                                value.length === 0 ||
                                value.some((person) => !person || !person.name || !person.lastName || !person.numberDoc)
                            ) {
                                callback('Complete los datos requeridos');
                                return;
                            }
                            callback();
                        },
                    },
                ]}
            />
        </>
    );

    const Step2Component = (
        <>
            <Row gutter={10}>
                <Col xs={{ span: 24 }}>
                    <Typography.Title className="title-data" level={4}>
                        {translate({ key: 'contracts.title-details' })}
                    </Typography.Title>
                </Col>
                <Col xs={{ span: 24 }} xl={{ span: 12 }}>
                    <Form.Item
                        name="date"
                        label={translate({ key: 'contracts.date' })}
                        labelAlign="left"
                        rules={[{ required: true }]}
                    >
                        <DatePicker
                            format={'DD/MM/YYYY'}
                            onChange={controller.initialDateChange}
                            getPopupContainer={fixSelectOnScroll}
                        />
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} xl={{ span: 12 }}>
                    <Form.Item
                        name="durationMonths"
                        label={translate({ key: 'contracts.duration' })}
                        labelAlign="left"
                        rules={[{ required: true }]}
                    >
                        <InputNumber minLength={0} onChange={controller.durationChange} type="number" />
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} xl={{ span: 12 }}>
                    <Form.Item>
                        <DatePicker
                            format={'DD/MM/YYYY'}
                            value={controller.finishDate}
                            placeholder={translate({ key: 'contracts.end-date' })}
                            disabled={true}
                            getPopupContainer={fixSelectOnScroll}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={10}>
                <Col xs={{ span: 24 }} xl={{ span: 12 }}>
                    <Form.Item
                        name="startPrice"
                        label={translate({ key: 'contracts.start-price' })}
                        labelAlign="left"
                        rules={[
                            {
                                required: true,
                                validator: (_, value, callback) => {
                                    if (!value || value === 0) {
                                        callback('Ingrese precio');
                                        return;
                                    }
                                    callback();
                                },
                            },
                        ]}
                    >
                        <CurrencyInput size={'large'} className="input-rent-amount" placeholder={''} />
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} xl={{ span: 12 }}>
                    <Form.Item
                        name="percentageByAdministration"
                        label={translate({ key: 'contracts.percentage-per-administration' })}
                        labelAlign="left"
                        rules={[{ required: true }]}
                    >
                        <Input type="number" addonAfter="%" />
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }}>
                    <NewOrOldContract
                        validate={controller.validateUpdateFrequency}
                        durationMonths={controller.duration}
                        initialDate={controller.initialDate}
                        updateFrequency={controller.initialValues.updateFrequency}
                    />
                </Col>
                <Col xs={{ span: 24 }} style={{ marginTop: '30px' }}>
                    <Upload
                        name="file"
                        action={`${process.env.REACT_APP_API_BASE_URL}/configuration/file`}
                        headers={{ Authorization: `Bearer ${session.getToken()}` }}
                        onChange={controller.handleChange}
                        multiple={false}
                    >
                        {controller.fileUrl != '' ? null : (
                            <Button type="link">{translate({ key: 'contracts.upload-file' })}</Button>
                        )}
                    </Upload>
                </Col>
            </Row>
            <Divider />
            <Row gutter={10}>
                <Col xs={{ span: 24 }}>
                    <SelectOrCreateItem
                        name={'guarantorsId'}
                        createFormItemName={'newGuarantors'}
                        selectOptions={controller.personsOptions}
                        selectMode={'multiple'}
                        optionFilterProp={'label'}
                        defaultCreateVisible={
                            props.extractedData?.guarantor
                                ? props.extractedData.guarantor.existingId == null &&
                                  props.extractedData.guarantor.name != null
                                : false
                        }
                        createComponent={
                            <CreatePersonsFields
                                showOptionalFields={guarantorHasOptionalData}
                                provincesOptions={controller.provincesOptions}
                            />
                        }
                        createTitle={translate({ key: 'contracts.guarantor-data' })}
                        label={translate({ key: 'contracts.guarantors' })}
                        requiredForm={false}
                        rulesComponent={[
                            {
                                validator: (_: any, value: any, callback: any) => {
                                    if (
                                        !Array.isArray(value) ||
                                        value.length === 0 ||
                                        value.some(
                                            (person) =>
                                                !person || !person.name || !person.lastName || !person.numberDoc,
                                        )
                                    ) {
                                        callback('Complete los datos requeridos');
                                        return;
                                    }
                                    callback();
                                },
                            },
                        ]}
                        onClickSelect={controller.onClickSelect}
                        isOpen={controller.selectIsOpen}
                        onBlurSelect={controller.onBlurSelect}
                    />
                </Col>
            </Row>
        </>
    );

    const Step3Component = (
        <>
            <Typography.Title className="title-data" level={4}>
                {translate({ key: 'contracts.title-interest' })}
            </Typography.Title>
            <InterestOwed />
        </>
    );

    return (
        <div>
            <Modal
                visible={props.isVisible}
                onCancel={() => {
                    controller.onCancel();
                    if (props.onCancel) {
                        props.onCancel();
                    }
                }}
                footer={null}
                destroyOnClose={true}
                maskClosable={false}
            >
                <StepperForm
                    title={translate({ key: 'contracts.add-contract' })}
                    form={controller.form}
                    onFinish={controller.onFormSubmit}
                    isLoading={controller.isLoading}
                    className={'add-contract'}
                    layout={'vertical'}
                    initialValues={controller.initialValues}
                    steps={[
                        {
                            key: 0,
                            validationFields: (values: any) => [
                                { name: 'propertyId', label: translate({ key: 'contracts.properties' }) },
                                { name: 'rentersId', label: translate({ key: 'contracts.renters' }) },
                                ...(values.propertyId === -1
                                    ? [
                                          {
                                              name: ['newProperty', 'street'],
                                              label: translate({ key: 'properties.street' }),
                                          },
                                          {
                                              name: ['newProperty', 'district'],
                                              label: translate({ key: 'properties.district' }),
                                          },
                                          {
                                              name: ['newProperty', 'city'],
                                              label: translate({ key: 'settings.city' }),
                                          },
                                          {
                                              name: ['newProperty', 'owners'],
                                              label: translate({ key: 'properties.owners' }),
                                          },
                                          ...(values.newProperty?.owners?.includes(-1)
                                              ? [
                                                    {
                                                        name: ['newProperty', 'newOwners'],
                                                        label: translate({ key: 'properties.owners' }),
                                                    },
                                                    ...(values.newProperty?.newOwners ?? []).flatMap(
                                                        (_: any, index: number) => [
                                                            {
                                                                name: ['newProperty', 'newOwners', index, 'name'],
                                                                label: 'Nombre del propietario',
                                                            },
                                                            {
                                                                name: ['newProperty', 'newOwners', index, 'lastName'],
                                                                label: 'Apellido del propietario',
                                                            },
                                                            {
                                                                name: ['newProperty', 'newOwners', index, 'numberDoc'],
                                                                label: 'Documento del propietario',
                                                            },
                                                            {
                                                                name: ['newProperty', 'newOwners', index, 'phone'],
                                                                label: 'Teléfono del propietario',
                                                            },
                                                            {
                                                                name: ['newProperty', 'newOwners', index, 'email'],
                                                                label: 'Email del propietario',
                                                            },
                                                        ],
                                                    ),
                                                ]
                                              : []),
                                      ]
                                    : []),
                                ...(values.rentersId?.includes(-1)
                                    ? [
                                          { name: 'newRenters', label: translate({ key: 'contracts.renters' }) },
                                          ...(values.newRenters ?? []).flatMap((_: any, index: number) => [
                                              { name: ['newRenters', index, 'name'], label: 'Nombre del inquilino' },
                                              {
                                                  name: ['newRenters', index, 'lastName'],
                                                  label: 'Apellido del inquilino',
                                              },
                                              {
                                                  name: ['newRenters', index, 'numberDoc'],
                                                  label: 'Documento del inquilino',
                                              },
                                              {
                                                  name: ['newRenters', index, 'phone'],
                                                  label: 'Teléfono del inquilino',
                                              },
                                              {
                                                  name: ['newRenters', index, 'email'],
                                                  label: 'Email del inquilino',
                                              },
                                          ]),
                                      ]
                                    : []),
                            ],
                            title: translate({ key: 'contracts.general-data' }),
                            component: Step1Component,
                            isValid: true,
                        },
                        {
                            key: 1,
                            validationFields: (values: any) => [
                                { name: 'date', label: translate({ key: 'contracts.date' }) },
                                { name: 'startPrice', label: translate({ key: 'contracts.start-price' }) },
                                { name: 'durationMonths', label: translate({ key: 'contracts.duration' }) },
                                {
                                    name: 'percentageByAdministration',
                                    label: translate({ key: 'contracts.percentage-per-administration' }),
                                },
                                { name: 'amountsActualizations', label: 'Montos de actualización' },
                                { name: 'amountsActualizationsNew', label: 'Monto actualizado vigente' },
                                {
                                    name: 'updateFrequency',
                                    label: translate({ key: 'contracts.update-periodicity' }),
                                },
                                ...(values.guarantorsId?.includes(-1)
                                    ? [
                                          { name: 'newGuarantors', label: translate({ key: 'contracts.guarantors' }) },
                                          ...(values.newGuarantors ?? []).flatMap((_: any, index: number) => [
                                              { name: ['newGuarantors', index, 'name'], label: 'Nombre del garante' },
                                              {
                                                  name: ['newGuarantors', index, 'lastName'],
                                                  label: 'Apellido del garante',
                                              },
                                              {
                                                  name: ['newGuarantors', index, 'numberDoc'],
                                                  label: 'Documento del garante',
                                              },
                                              {
                                                  name: ['newGuarantors', index, 'phone'],
                                                  label: 'Teléfono del garante',
                                              },
                                              {
                                                  name: ['newGuarantors', index, 'email'],
                                                  label: 'Email del garante',
                                              },
                                          ]),
                                      ]
                                    : []),
                            ],
                            title: translate({ key: 'contracts.contract-detail' }),
                            component: Step2Component,
                            isValid: true,
                        },
                        {
                            key: 2,
                            validationFields: [
                                { name: 'expiredDay', label: translate({ key: 'contracts.expired-day' }) },
                                { name: 'debtCollection', label: translate({ key: 'contracts.debt-collection' }) },
                                { name: 'typeOfCharge', label: 'Tipo de cobro por mora' },
                                { name: 'initialDayInterest', label: translate({ key: 'contracts.initial-day' }) },
                            ],
                            title: translate({ key: 'contracts.expired' }),
                            component: Step3Component,
                            isValid: true,
                        },
                    ]}
                    previousButtonLabel={translate({ key: 'contracts.back' })}
                    nextButtonLabel={translate({ key: 'contracts.next' })}
                    lastButtonLabel={translate({ key: 'contracts.add' })}
                />
            </Modal>
        </div>
    );
};
